// =====================================================
// ANÁLISIS SATELITAL CON IA
// Componente para análisis comparativo de imágenes satelitales
// src/pages/Agro/Productor/AnalisisSatelitalIA.js
// =====================================================
// CARACTERÍSTICAS:
// - Selección de establecimiento y lote
// - Comparativa antes/después de imágenes satelitales
// - Selección AUTOMÁTICA del método de análisis:
//   1. Intenta IA Externa (Claude) via backend
//   2. Si Claude no está habilitado, backend usa LOCAL
//   3. Si hay error de red, frontend usa análisis JS local
// - Exportación de resultados para PDF
// =====================================================
// VERSIÓN: 1.2 - Selección automática de método
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Alert,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import dayjs from "dayjs"
import { format, subMonths } from "date-fns"
import { es } from "date-fns/locale"

import {
  getEstablecimientos,
  getPoligonosByEstablecimiento,
  getImagenPoligono,
  getSerieNdviPoligono,
  generarAnalisisIASatelital,
  getUltimoAnalisisPoligono,
} from "api/agro"
import toastHandler from "toastHandler"
import { getErrorMessage } from "helpers/error_helpers"

// =====================================================
// ESTILOS
// =====================================================

const STYLES = {
  headerSatelital: {
    background: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
    color: "#fff",
    padding: "16px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
  },
  headerAnalisisLocal: {
    background: "linear-gradient(135deg, #4a148c 0%, #7b1fa2 100%)",
    color: "#fff",
    padding: "16px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
  },
  headerAnalisisIA: {
    background: "linear-gradient(135deg, #00695c 0%, #00897b 100%)",
    color: "#fff",
    padding: "16px 20px",
    borderRadius: "8px 8px 0 0",
    fontWeight: 600,
  },
  comparativaContainer: {
    display: "flex",
    gap: "20px",
    justifyContent: "center",
    alignItems: "stretch",
  },
  imagenComparativa: {
    flex: 1,
    maxWidth: "45%",
    textAlign: "center",
  },
  imagenSatelital: {
    width: "100%",
    height: "auto",
    maxHeight: "350px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "2px solid #dee2e6",
    cursor: "pointer",
  },
  flecha: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "2rem",
    color: "#6c757d",
  },
  analisisLocal: {
    background: "#f3e5f5",
    borderRadius: "8px",
    padding: "20px",
    borderLeft: "4px solid #7b1fa2",
  },
  analisisIA: {
    background: "#e0f2f1",
    borderRadius: "8px",
    padding: "20px",
    borderLeft: "4px solid #00897b",
  },
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const AnalisisSatelitalIA = ({ idProductor, onAnalisisGenerado }) => {
  // Estados de selección
  const [establecimientos, setEstablecimientos] = useState([])
  const [poligonos, setPoligonos] = useState([])
  const [establecimientoId, setEstablecimientoId] = useState("")
  const [poligonoId, setPoligonoId] = useState("")

  // Estados de fechas
  const [fechaAntes, setFechaAntes] = useState(
    format(subMonths(new Date(), 3), "yyyy-MM-dd"),
  )
  const [fechaDespues, setFechaDespues] = useState(
    format(new Date(), "yyyy-MM-dd"),
  )

  // Estados de imágenes
  const [imagenAntes, setImagenAntes] = useState(null)
  const [imagenDespues, setImagenDespues] = useState(null)
  const [serieNdvi, setSerieNdvi] = useState([])

  // Estados de carga y análisis
  const [loadingEstablecimientos, setLoadingEstablecimientos] = useState(true)
  const [loadingPoligonos, setLoadingPoligonos] = useState(false)
  const [loadingImagenes, setLoadingImagenes] = useState(false)
  const [loadingAnalisis, setLoadingAnalisis] = useState(false)
  const [loadingUltimoAnalisis, setLoadingUltimoAnalisis] = useState(false)
  const [analisisResultado, setAnalisisResultado] = useState(null)
  const [ultimoAnalisisExistente, setUltimoAnalisisExistente] = useState(null)
  const [error, setError] = useState(null)

  // Modal de imagen ampliada
  const [imagenAmpliada, setImagenAmpliada] = useState(null)

  const setSuccess = toastHandler(state => state.setSuccess)
  // const setError = toastHandler(state => state.setError)
  const setWarnings = toastHandler(state => state.setWarnings)

  // =====================================================
  // CARGA INICIAL
  // =====================================================

  useEffect(() => {
    if (idProductor) {
      cargarEstablecimientos()
    }
  }, [idProductor])

  const cargarEstablecimientos = async () => {
    setLoadingEstablecimientos(true)
    try {
      const data = await getEstablecimientos(idProductor)
      setEstablecimientos(data || [])

      if (data && data.length === 1) {
        setEstablecimientoId(data[0].id.toString())
      }
    } catch (err) {
      console.error("Error cargando establecimientos:", err)
      setError("Error al cargar establecimientos")
    } finally {
      setLoadingEstablecimientos(false)
    }
  }

  // =====================================================
  // CARGA DE POLÍGONOS
  // =====================================================

  useEffect(() => {
    if (establecimientoId) {
      cargarPoligonos()
    } else {
      setPoligonos([])
      setPoligonoId("")
    }
  }, [establecimientoId])

  const cargarPoligonos = async () => {
    setLoadingPoligonos(true)
    try {
      const data = await getPoligonosByEstablecimiento(establecimientoId)
      setPoligonos(data || [])

      if (data && data.length === 1) {
        setPoligonoId(data[0].id.toString())
      }
    } catch (err) {
      console.error("Error cargando polígonos:", err)
    } finally {
      setLoadingPoligonos(false)
    }
  }

  // =====================================================
  // CARGA DE ÚLTIMO ANÁLISIS AL SELECCIONAR POLÍGONO
  // =====================================================

  useEffect(() => {
    if (poligonoId) {
      cargarUltimoAnalisis()
    } else {
      setUltimoAnalisisExistente(null)
      setAnalisisResultado(null)
    }
  }, [poligonoId])

  const cargarUltimoAnalisis = async () => {
    setLoadingUltimoAnalisis(true)
    setUltimoAnalisisExistente(null)

    try {
      const ultimo = await getUltimoAnalisisPoligono(idProductor, poligonoId)
      if (ultimo) {
        console.log("Último análisis encontrado:", ultimo)
        setUltimoAnalisisExistente(ultimo)
        // Mostrar automáticamente el último análisis
        setAnalisisResultado(ultimo)
      }
    } catch (err) {
      // 404 es esperado si no hay análisis previo
      if (err.response?.status !== 404) {
        console.error("Error cargando último análisis:", err)
      }
    } finally {
      setLoadingUltimoAnalisis(false)
    }
  }

  // =====================================================
  // CARGA DE IMÁGENES COMPARATIVAS
  // =====================================================

  const cargarImagenesComparativas = async () => {
    if (!establecimientoId || !poligonoId) {
      setError("Seleccione un establecimiento y un lote")
      return
    }

    setLoadingImagenes(true)
    setError(null)
    setAnalisisResultado(null)

    try {
      const imgAntes = await getImagenPoligono(
        establecimientoId,
        poligonoId,
        fechaAntes,
      )
      setImagenAntes(imgAntes)

      const imgDespues = await getImagenPoligono(
        establecimientoId,
        poligonoId,
        fechaDespues,
      )
      setImagenDespues(imgDespues)

      const serie = await getSerieNdviPoligono(
        poligonoId,
        fechaAntes,
        fechaDespues,
      )
      setSerieNdvi(serie || [])
    } catch (err) {
      console.error("Error cargando imágenes:", err)
      // BDSJEA-460: si el backend explica la causa (p. ej. lote sin geometría) se muestra tal cual
      setError(
        getErrorMessage(err, {
          fallback:
            "Error al cargar las imágenes satelitales. Verifique las fechas seleccionadas.",
        }),
      )
    } finally {
      setLoadingImagenes(false)
    }
  }

  // =====================================================
  // GENERACIÓN DE ANÁLISIS - AUTOMÁTICO
  // Flujo: Backend (IA_EXT → LOCAL) → Fallback JS
  // =====================================================

  const generarAnalisis = async () => {
    if (!imagenAntes || !imagenDespues) {
      setError("Primero cargue las imágenes comparativas")
      return
    }

    setLoadingAnalisis(true)
    setError(null)

    try {
      // Preparar datos para el análisis
      const datosAnalisis = {
        idProductor,
        idEstablecimiento: parseInt(establecimientoId),
        idPoligono: parseInt(poligonoId),
        fechaAntes,
        fechaDespues,
        imagenAntes: {
          fecha: imagenAntes.fecha || fechaAntes,
          ndviPromedio: imagenAntes.ndviPromedio,
          coberturaNubes: imagenAntes.coberturaNubes,
          urlTrueColor: imagenAntes.urlTrueColor,
          urlNdvi: imagenAntes.urlNdvi,
        },
        imagenDespues: {
          fecha: imagenDespues.fecha || fechaDespues,
          ndviPromedio: imagenDespues.ndviPromedio,
          coberturaNubes: imagenDespues.coberturaNubes,
          urlTrueColor: imagenDespues.urlTrueColor,
          urlNdvi: imagenDespues.urlNdvi,
        },
        serieNdvi: serieNdvi.map(s => ({
          fecha: s.fechaCaptura,
          ndvi: s.ndviPromedio,
        })),
        poligono: poligonos.find(p => p.id.toString() === poligonoId),
      }

      // Llamar al backend - El backend decide automáticamente:
      // Si Claude está habilitado → usa IA_EXT
      // Si no → usa LOCAL
      const resultado = await generarAnalisisIASatelital(
        idProductor,
        datosAnalisis,
      )

      // Debug: mostrar el método recibido
      console.log("Análisis recibido del backend:", resultado)
      console.log("Método detectado:", resultado.metodo)

      setAnalisisResultado(resultado)
      notificarResultado(resultado)

      // Detectar método - puede venir como "metodo" o "metodoAnalisis"
      const metodo = resultado.metodo || resultado.metodoAnalisis
      console.log("Método final para UI:", metodo)

      if (metodo === "IA_EXT") {
        setSuccess("Análisis generado con IA (Claude)")
      } else if (metodo === "LOCAL") {
        setSuccess("Análisis generado correctamente")
      } else {
        setSuccess("Análisis generado")
      }
    } catch (err) {
      console.error("Error al consultar backend, usando análisis local:", err)

      // Fallback: Si el backend falla, usar análisis local del frontend
      const analisisLocal = generarAnalisisLocalJS()
      setAnalisisResultado(analisisLocal)
      notificarResultado(analisisLocal)

      setWarnings("Análisis generado localmente (sin conexión al servidor)")
    } finally {
      setLoadingAnalisis(false)
    }
  }

  // Notificar al componente padre
  const notificarResultado = resultado => {
    if (onAnalisisGenerado) {
      onAnalisisGenerado({
        ...resultado,
        imagenAntes,
        imagenDespues,
        fechaAntes,
        fechaDespues,
      })
    }
  }

  // =====================================================
  // ANÁLISIS LOCAL JS - Fallback cuando backend no responde
  // =====================================================

  const generarAnalisisLocalJS = () => {
    const ndviAntes = imagenAntes?.ndviPromedio || 0
    const ndviDespues = imagenDespues?.ndviPromedio || 0
    const variacion =
      ndviAntes > 0 ? ((ndviDespues - ndviAntes) / ndviAntes) * 100 : 0

    let tendencia = "estable"
    let estadoVegetacion = "normal"
    let recomendaciones = []

    if (variacion > 15) {
      tendencia = "mejora significativa"
      estadoVegetacion = "saludable"
      recomendaciones = [
        "El cultivo muestra un desarrollo favorable",
        "Mantener las prácticas actuales de manejo",
        "Considerar optimización del riego si aplica",
      ]
    } else if (variacion > 5) {
      tendencia = "leve mejora"
      estadoVegetacion = "en desarrollo"
      recomendaciones = [
        "El cultivo presenta desarrollo normal",
        "Monitorear evolución en próximas semanas",
      ]
    } else if (variacion < -15) {
      tendencia = "deterioro significativo"
      estadoVegetacion = "estrés"
      recomendaciones = [
        "Se recomienda inspección presencial del lote",
        "Verificar posibles problemas de plagas o enfermedades",
        "Evaluar condiciones de humedad del suelo",
        "Considerar análisis de suelo",
      ]
    } else if (variacion < -5) {
      tendencia = "leve deterioro"
      estadoVegetacion = "atención"
      recomendaciones = [
        "Monitorear evolución del cultivo",
        "Verificar condiciones climáticas recientes",
      ]
    } else {
      recomendaciones = [
        "El cultivo se mantiene estable",
        "Continuar con monitoreo regular",
      ]
    }

    const poligonoSeleccionado = poligonos.find(
      p => p.id.toString() === poligonoId,
    )

    return {
      resumen: `El análisis comparativo de ${poligonoSeleccionado?.nombreLote || "el lote"} entre ${format(new Date(fechaAntes), "dd/MM/yyyy")} y ${format(new Date(fechaDespues), "dd/MM/yyyy")} muestra una ${tendencia} en el índice de vegetación (NDVI). El estado actual de la vegetación es: ${estadoVegetacion}.`,
      tendencia,
      variacionNdvi: variacion,
      ndviInicial: ndviAntes,
      ndviFinal: ndviDespues,
      estadoVegetacion,
      recomendaciones,
      fechaGeneracion: new Date().toISOString(),
      metodo: "LOCAL_JS",
      confianzaAnalisis: 70,
    }
  }

  // =====================================================
  // FUNCIONES AUXILIARES
  // =====================================================

  const getNdviEstado = valor => {
    if (valor >= 0.6) return { texto: "Excelente", color: "success" }
    if (valor >= 0.4) return { texto: "Bueno", color: "info" }
    if (valor >= 0.2) return { texto: "Regular", color: "warning" }
    return { texto: "Bajo", color: "danger" }
  }

  const getNdviColor = valor => {
    if (valor >= 0.6) return "#28a745"
    if (valor >= 0.4) return "#17a2b8"
    if (valor >= 0.2) return "#ffc107"
    return "#dc3545"
  }

  const getMetodoInfo = metodo => {
    switch (metodo) {
      case "IA_EXT":
        return { texto: "Claude AI", color: "success", icono: "mdi-robot" }
      case "LOCAL":
        return {
          texto: "Análisis Backend",
          color: "primary",
          icono: "mdi-server",
        }
      case "LOCAL_JS":
        return {
          texto: "Análisis Local",
          color: "warning",
          icono: "mdi-laptop",
        }
      default:
        return {
          texto: "Análisis",
          color: "secondary",
          icono: "mdi-chart-line",
        }
    }
  }

  const getHeaderStyle = metodo => {
    if (metodo === "IA_EXT") return STYLES.headerAnalisisIA
    return STYLES.headerAnalisisLocal
  }

  const getResumenStyle = metodo => {
    if (metodo === "IA_EXT") return STYLES.analisisIA
    return STYLES.analisisLocal
  }

  // =====================================================
  // RENDER
  // =====================================================

  if (loadingEstablecimientos) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">Cargando establecimientos...</p>
      </div>
    )
  }

  return (
    <div>
      {/* Error global */}
      {error && (
        <Alert color="danger" className="mb-4">
          <i className="mdi mdi-alert-circle me-2" />
          {error}
        </Alert>
      )}

      {/* Selectores de Establecimiento y Lote */}
      <Card className="mb-4 border-0 shadow-sm">
        <div style={STYLES.headerSatelital}>
          <i className="mdi mdi-satellite-variant me-2" />
          Análisis Satelital Inteligente
        </div>
        <CardBody>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Establecimiento</Label>
                <Input
                  type="select"
                  value={establecimientoId}
                  onChange={e => setEstablecimientoId(e.target.value)}
                >
                  <option value="">Seleccione...</option>
                  {establecimientos.map(est => (
                    <option key={est.id} value={est.id}>
                      {est.nombre}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Lote / Polígono</Label>
                <Input
                  type="select"
                  value={poligonoId}
                  onChange={e => setPoligonoId(e.target.value)}
                  disabled={!establecimientoId || loadingPoligonos}
                >
                  <option value="">
                    {loadingPoligonos ? "Cargando..." : "Seleccione..."}
                  </option>
                  {poligonos.map(pol => (
                    <option key={pol.id} value={pol.id}>
                      {pol.nombreLote || pol.nombre} (
                      {pol.superficieHa?.toFixed(1)} ha)
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Uso Actual</Label>
                <Input
                  type="text"
                  value={
                    poligonos.find(p => p.id.toString() === poligonoId)
                      ?.usoActual || "-"
                  }
                  disabled
                  className="bg-light"
                />
              </FormGroup>
            </Col>
          </Row>

          {/* Indicador de carga/existencia de análisis previo */}
          {poligonoId && (
            <Row className="mb-3">
              <Col>
                {loadingUltimoAnalisis ? (
                  <Alert color="secondary" className="py-2 mb-0">
                    <Spinner size="sm" className="me-2" />
                    Buscando análisis previos...
                  </Alert>
                ) : ultimoAnalisisExistente ? (
                  <Alert color="success" className="py-2 mb-0">
                    <i className="mdi mdi-check-circle me-2" />
                    <strong>Análisis previo encontrado</strong> - Generado el{" "}
                    {dayjs(ultimoAnalisisExistente.fechaGeneracion).format(
                      "DD/MM/YYYY HH:mm",
                    )}{" "}
                    ({ultimoAnalisisExistente.tendencia})
                  </Alert>
                ) : (
                  <Alert color="warning" className="py-2 mb-0">
                    <i className="mdi mdi-alert-circle-outline me-2" />
                    No hay análisis previos para este lote. Cargue las imágenes
                    y genere uno nuevo.
                  </Alert>
                )}
              </Col>
            </Row>
          )}

          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Fecha Inicial (Antes)</Label>
                <Input
                  type="date"
                  value={fechaAntes}
                  onChange={e => setFechaAntes(e.target.value)}
                  max={fechaDespues}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Fecha Final (Después)</Label>
                <Input
                  type="date"
                  value={fechaDespues}
                  onChange={e => setFechaDespues(e.target.value)}
                  min={fechaAntes}
                  max={format(new Date(), "yyyy-MM-dd")}
                />
              </FormGroup>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button
                color="primary"
                onClick={cargarImagenesComparativas}
                disabled={!establecimientoId || !poligonoId || loadingImagenes}
                className="w-100"
              >
                {loadingImagenes ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Cargando...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-image-search me-2" />
                    Cargar Imágenes
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Imágenes Comparativas */}
      {(imagenAntes || imagenDespues) && (
        <Card className="mb-4 border-0 shadow-sm">
          <CardBody>
            <h6 className="mb-4">
              <i className="mdi mdi-compare me-2" />
              Comparativa de Imágenes Satelitales
            </h6>

            <div style={STYLES.comparativaContainer}>
              {/* Imagen Antes */}
              <div style={STYLES.imagenComparativa}>
                <Badge color="secondary" className="mb-2">
                  ANTES
                </Badge>
                <p className="text-muted small mb-2">
                  {format(new Date(fechaAntes), "dd MMM yyyy", { locale: es })}
                </p>
                {imagenAntes?.urlTrueColor ? (
                  <img
                    src={imagenAntes.urlTrueColor}
                    alt="Imagen antes"
                    style={STYLES.imagenSatelital}
                    onClick={() => setImagenAmpliada(imagenAntes)}
                  />
                ) : (
                  <div
                    className="bg-light d-flex align-items-center justify-content-center rounded"
                    style={{ height: 250 }}
                  >
                    <span className="text-muted">Sin imagen disponible</span>
                  </div>
                )}
                {imagenAntes?.ndviPromedio !== undefined && (
                  <div className="mt-2">
                    <Badge
                      color={getNdviEstado(imagenAntes.ndviPromedio).color}
                    >
                      NDVI: {imagenAntes.ndviPromedio?.toFixed(3)}
                    </Badge>
                  </div>
                )}
              </div>

              <div style={STYLES.flecha}>
                <i className="mdi mdi-arrow-right-bold" />
              </div>

              {/* Imagen Después */}
              <div style={STYLES.imagenComparativa}>
                <Badge color="primary" className="mb-2">
                  DESPUÉS
                </Badge>
                <p className="text-muted small mb-2">
                  {format(new Date(fechaDespues), "dd MMM yyyy", {
                    locale: es,
                  })}
                </p>
                {imagenDespues?.urlTrueColor ? (
                  <img
                    src={imagenDespues.urlTrueColor}
                    alt="Imagen después"
                    style={STYLES.imagenSatelital}
                    onClick={() => setImagenAmpliada(imagenDespues)}
                  />
                ) : (
                  <div
                    className="bg-light d-flex align-items-center justify-content-center rounded"
                    style={{ height: 250 }}
                  >
                    <span className="text-muted">Sin imagen disponible</span>
                  </div>
                )}
                {imagenDespues?.ndviPromedio !== undefined && (
                  <div className="mt-2">
                    <Badge
                      color={getNdviEstado(imagenDespues.ndviPromedio).color}
                    >
                      NDVI: {imagenDespues.ndviPromedio?.toFixed(3)}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {/* Variación NDVI */}
            {imagenAntes?.ndviPromedio && imagenDespues?.ndviPromedio && (
              <div className="text-center mt-4 pt-3 border-top">
                <Row className="justify-content-center">
                  <Col md={3}>
                    <small className="text-muted d-block">NDVI Inicial</small>
                    <h4
                      style={{ color: getNdviColor(imagenAntes.ndviPromedio) }}
                    >
                      {imagenAntes.ndviPromedio.toFixed(3)}
                    </h4>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted d-block">NDVI Final</small>
                    <h4
                      style={{
                        color: getNdviColor(imagenDespues.ndviPromedio),
                      }}
                    >
                      {imagenDespues.ndviPromedio.toFixed(3)}
                    </h4>
                  </Col>
                  <Col md={3}>
                    <small className="text-muted d-block">Variación</small>
                    {(() => {
                      const variacion =
                        ((imagenDespues.ndviPromedio -
                          imagenAntes.ndviPromedio) /
                          imagenAntes.ndviPromedio) *
                        100
                      return (
                        <h4
                          className={
                            variacion >= 0 ? "text-success" : "text-danger"
                          }
                        >
                          {variacion >= 0 ? "+" : ""}
                          {variacion.toFixed(1)}%
                        </h4>
                      )
                    })()}
                  </Col>
                </Row>
              </div>
            )}

            {/* Botón Generar Análisis */}
            <div className="text-center mt-4">
              <Button
                color="success"
                size="lg"
                onClick={generarAnalisis}
                disabled={loadingAnalisis || (!imagenAntes && !imagenDespues)}
              >
                {loadingAnalisis ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Analizando con IA...
                  </>
                ) : (
                  <>
                    <i className="mdi mdi-robot me-2" />
                    Generar Análisis Inteligente
                  </>
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Resultado del Análisis */}
      {analisisResultado && (
        <Card className="mb-4 border-0 shadow-sm text-muted">
          <div
            style={getHeaderStyle(
              analisisResultado.metodo || analisisResultado.metodoAnalisis,
            )}
          >
            <i className="mdi mdi-brain me-2" />
            Análisis Inteligente
            {(() => {
              const info = getMetodoInfo(
                analisisResultado.metodo || analisisResultado.metodoAnalisis,
              )
              return (
                <Badge color={info.color} className="ms-2">
                  <i className={`mdi ${info.icono} me-1`} />
                  {info.texto}
                </Badge>
              )
            })()}
            {/* Indicador de análisis previo */}
            {ultimoAnalisisExistente &&
              analisisResultado.id === ultimoAnalisisExistente.id && (
                <Badge color="info" className="ms-2">
                  <i className="mdi mdi-history me-1" />
                  Análisis Previo
                </Badge>
              )}
          </div>
          <CardBody>
            {/* Aviso de análisis previo */}
            {ultimoAnalisisExistente &&
              analisisResultado.id === ultimoAnalisisExistente.id && (
                <Alert color="info" className="mb-3">
                  <i className="mdi mdi-information-outline me-2" />
                  Este es el último análisis realizado para este lote. Puede
                  generar uno nuevo seleccionando las fechas y haciendo clic en
                  Generar Análisis Inteligente.
                </Alert>
              )}

            {/* Resumen */}
            <div
              style={getResumenStyle(
                analisisResultado.metodo || analisisResultado.metodoAnalisis,
              )}
              className="mb-4"
            >
              <h6 className="mb-3">
                <i className="mdi mdi-text-box-outline me-2" />
                Resumen del Análisis
              </h6>
              <p className="mb-0">{analisisResultado.resumen}</p>
            </div>

            {/* Diagnóstico detallado (solo IA externa) */}
            {analisisResultado.diagnosticoDetallado && (
              <Alert color="info" className="mb-4">
                <h6 className="alert-heading">
                  <i className="mdi mdi-file-document-outline me-2" />
                  Diagnóstico Detallado
                </h6>
                <p className="mb-0">{analisisResultado.diagnosticoDetallado}</p>
              </Alert>
            )}

            {/* Alertas (solo IA externa) */}
            {analisisResultado.alertas &&
              analisisResultado.alertas.length > 0 && (
                <Alert color="warning" className="mb-4">
                  <h6 className="alert-heading">
                    <i className="mdi mdi-alert me-2" />
                    Alertas
                  </h6>
                  <ul className="mb-0">
                    {analisisResultado.alertas.map((alerta, idx) => (
                      <li key={idx}>{alerta}</li>
                    ))}
                  </ul>
                </Alert>
              )}

            {/* Indicadores */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="border-0 bg-light text-center">
                  <CardBody className="py-3">
                    <small className="text-muted d-block">Tendencia</small>
                    <Badge
                      color={
                        analisisResultado.tendencia?.includes("mejora")
                          ? "success"
                          : analisisResultado.tendencia?.includes("deterioro")
                            ? "danger"
                            : "secondary"
                      }
                      className="px-3 py-2 mt-1"
                    >
                      {analisisResultado.tendencia}
                    </Badge>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light text-center">
                  <CardBody className="py-3">
                    <small className="text-muted d-block">
                      Estado Vegetación
                    </small>
                    <Badge
                      color={
                        analisisResultado.estadoVegetacion === "saludable"
                          ? "success"
                          : analisisResultado.estadoVegetacion === "estrés"
                            ? "danger"
                            : analisisResultado.estadoVegetacion === "atención"
                              ? "warning"
                              : "info"
                      }
                      className="px-3 py-2 mt-1"
                    >
                      {analisisResultado.estadoVegetacion}
                    </Badge>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light text-center">
                  <CardBody className="py-3">
                    <small className="text-muted d-block">Variación NDVI</small>
                    <h5
                      className={`mb-0 ${analisisResultado.variacionNdvi >= 0 ? "text-success" : "text-danger"}`}
                    >
                      {analisisResultado.variacionNdvi >= 0 ? "+" : ""}
                      {analisisResultado.variacionNdvi?.toFixed(1)}%
                    </h5>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 bg-light text-center">
                  <CardBody className="py-3">
                    <small className="text-muted d-block">Confianza</small>
                    <h5 className="mb-0">
                      {analisisResultado.confianzaAnalisis ||
                        analisisResultado.confianza ||
                        "-"}
                      %
                    </h5>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Recomendaciones */}
            {analisisResultado.recomendaciones &&
              analisisResultado.recomendaciones.length > 0 && (
                <div>
                  <h6 className="mb-3">
                    <i className="mdi mdi-lightbulb-outline me-2 text-warning" />
                    Recomendaciones
                  </h6>
                  <ul className="mb-0">
                    {analisisResultado.recomendaciones.map((rec, idx) => (
                      <li key={idx} className="mb-2">
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Metadata */}
            <div className="text-muted small mt-4 pt-3 border-top d-flex justify-content-between">
              <span>
                <i className="mdi mdi-clock-outline me-1" />
                Generado el{" "}
                {dayjs(analisisResultado.fechaGeneracion).format(
                  "DD/MM/YYYY HH:mm",
                )}
              </span>
              {analisisResultado.modeloIA && (
                <span>
                  <i className="mdi mdi-cog me-1" />
                  Modelo: {analisisResultado.modeloIA}
                </span>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modal imagen ampliada */}
      <Modal
        isOpen={!!imagenAmpliada}
        toggle={() => setImagenAmpliada(null)}
        size="xl"
      >
        <ModalHeader toggle={() => setImagenAmpliada(null)}>
          Imagen Satelital
        </ModalHeader>
        <ModalBody className="text-center">
          {imagenAmpliada && (
            <>
              <img
                src={imagenAmpliada.urlTrueColor}
                alt="Imagen ampliada"
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
              <div className="mt-3">
                {imagenAmpliada.ndviPromedio !== undefined && (
                  <Badge
                    color={getNdviEstado(imagenAmpliada.ndviPromedio).color}
                    className="me-2"
                  >
                    NDVI: {imagenAmpliada.ndviPromedio.toFixed(3)}
                  </Badge>
                )}
                {imagenAmpliada.coberturaNubes !== undefined && (
                  <Badge color="secondary">
                    Nubes: {imagenAmpliada.coberturaNubes}%
                  </Badge>
                )}
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default AnalisisSatelitalIA
